import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Link } from 'react-router-dom'
import { Button } from '@/shared/ui/Button'
import { Modal } from '@/shared/ui/Modal'
import { Pagination } from '@/shared/ui/Pagination'
import { Empty } from '@/shared/ui/Panel'
import { Strip } from '@/shared/ui/Strip'
import type { WithdrawalResponse } from '@/shared/api/types'
import { coinOf, networkOf } from '@/shared/lib/currencies'
import { explain } from '@/shared/lib/errors'
import { crypto, shortAddress } from '@/shared/lib/format'
import { useMeta } from '@/shared/lib/hooks/useMeta'
import { isCancelable, WITHDRAWAL_VIEW } from '@/stores/WithdrawalsStore'
import { useStores } from '@/stores/RootStore'
import page from './Page.module.css'
import styles from './Withdrawals.module.css'

/**
 * Заявки на вывод.
 *
 * Экрана нет в макете, но без него шлюз неполон: заявку можно создать
 * и нельзя ни посмотреть, ни отозвать. Пока она ждёт одобрения, деньги
 * уже списаны с доступного баланса — а человек, ошибшийся суммой,
 * не имеет способа вернуть их, кроме как писать в поддержку.
 */
export const WithdrawalsPage = observer(function WithdrawalsPage() {
  useMeta('Заявки на вывод')
  const { withdrawals: store } = useStores()

  useEffect(() => { void store.page.load() }, [store])

  return (
    <div className={page.wide}>
      <header className={page.head}>
        <h1 className={styles.h1}>Заявки на вывод</h1>
      </header>

      <section className={styles.card}>
        {store.page.error && !store.page.loaded ? (
          <Strip tone="bad"
                 aside={<Button variant="secondary" onClick={() => void store.page.load()}>Ещё раз</Button>}>
            {explain(store.page.error)}
          </Strip>
        ) : null}

        {store.items.length === 0 && store.page.loaded ? (
          <Empty title="Заявок на вывод нет"
                 action={<Link className={styles.link} to="/app">К обзору</Link>} />
        ) : (
          <>
            <ul className={styles.list}>
              {store.items.map((item) => <Row key={item.id} item={item} />)}
            </ul>
            <Pagination total={store.page.value?.total ?? 0} limit={store.limit}
                        offset={store.offset}
                        onChange={(offset) => store.goToOffset(offset)} />
          </>
        )}
      </section>

      <CancelDialog />
    </div>
  )
})

const Row = observer(function Row({ item }: { item: WithdrawalResponse }) {
  const { withdrawals: store } = useStores()
  const coin = coinOf(item.currency)
  const view = WITHDRAWAL_VIEW[item.status]

  return (
    <li className={styles.row}>
      <span className={styles.main}>
        <span className={styles.amount}>{crypto(item.amount)} {coin.ticker}</span>
        <span className={styles.meta}>
          {networkOf(item.currency)} · {shortAddress(item.address, 10, 6)}
        </span>
        <span className={styles.meta}>{when(item.created_at)}</span>
        {/* Причина отказа — единственное, что объясняет исчезнувшую
            заявку. Без неё человек видит «Отклонена» и идёт в поддержку. */}
        {item.failure_reason ? (
          <span className={styles.why}>{item.failure_reason}</span>
        ) : null}
        {item.txid ? (
          <span className={styles.mono}>TxID {shortAddress(item.txid, 10, 6)}</span>
        ) : null}
      </span>

      <span className={styles.side}>
        <span className={`${styles.status} ${styles[view.tone]}`}>
          <span className={styles.dot} aria-hidden="true" /> {view.text}
        </span>
        {isCancelable(item.status) ? (
          <Button variant="secondary" onClick={() => store.ask(item)}>Отменить</Button>
        ) : null}
      </span>
    </li>
  )
})

const CancelDialog = observer(function CancelDialog() {
  const { withdrawals: store } = useStores()
  const target = store.canceling
  if (target === null) return null
  const coin = coinOf(target.currency)

  return (
    <Modal title="Отменить заявку?" onClose={() => store.close()}
           actions={
             <>
               <Button variant="secondary" onClick={() => store.close()}>Не отменять</Button>
               <Button variant="primary" busy={store.busy}
                       onClick={() => void store.cancel()}>Да, отменить</Button>
             </>
           }>
      <p className={styles.ask}>
        {crypto(target.amount)} {coin.ticker} на {shortAddress(target.address, 8, 6)}
      </p>
    </Modal>
  )
})

function when(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const d = new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(date)
  const t = new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit', minute: '2-digit',
  }).format(date)
  return `${d} / ${t}`
}
